/**
 * Push notification campaigns — admin-composed pushes sent to a member
 * segment from the "Push notifications" section. Tables and RPCs:
 * supabase/migrations/20260920120000_push_campaigns.sql.
 */

export type PushAudienceMembership = 'any' | 'active' | 'expired' | 'none';

/**
 * A segment definition, exactly as validate_push_audience() accepts it. Keys
 * are optional and an absent key means "no filter on this"; an unknown key is
 * rejected by the database, never ignored.
 */
export interface PushAudience {
  membership?: PushAudienceMembership;
  /** Plan names (memberships.name), matched case-insensitively. */
  plans?: string[];
  /** memberships.member_type values (paid, complimentary, staff, coach, …). */
  member_types?: string[];
  checked_in_within_days?: number;
  not_checked_in_within_days?: number;
  joined_within_days?: number;
  /**
   * Members the admin unticked in the composer's list. Applied by the
   * database resolver, so the count, the send and the stored campaign all
   * honour it — never subtracted client-side.
   */
  exclude_member_ids?: string[];
}

export const PUSH_EXCLUDE_MAX = 5000;

export const PUSH_TITLE_MAX = 100;
export const PUSH_BODY_MAX = 500;
export const PUSH_AUDIENCE_DAYS_MIN = 1;
export const PUSH_AUDIENCE_DAYS_MAX = 3650;

export type PushCampaignStatus = 'sending' | 'sent' | 'partial' | 'failed';

export interface PushCampaign {
  id: string;
  title: string;
  body: string;
  audience: PushAudience;
  audience_label: string | null;
  status: PushCampaignStatus;
  members_matched: number;
  members_reachable: number;
  tokens_targeted: number;
  sent_count: number;
  failed_count: number;
  tokens_removed: number;
  /** Devices whose Expo receipt came back ok — Apple/Google took the message. */
  delivered_count: number;
  /** Devices whose Expo receipt came back as an error (incl. device gone). */
  undelivered_count: number;
  /** Set once every accepted device has a final receipt; null while checking. */
  receipts_completed_at: string | null;
  last_error: string | null;
  created_by_admin_id: string | null;
  created_by_name: string;
  created_by_email: string;
  created_at: string;
  sent_at: string | null;
  updated_at: string;
}

export type PushCampaignRecipientStatus = 'pending' | 'sent' | 'failed' | 'device_not_registered';

/**
 * The Expo receipt for an accepted ticket, fetched ~15 min after sending by
 * the push-receipts cron function. `unavailable` = none came within 24 h.
 */
export type PushReceiptStatus = 'delivered' | 'failed' | 'device_not_registered' | 'unavailable';

export interface PushCampaignRecipient {
  id: string;
  campaign_id: string;
  member_id: string;
  platform: 'ios' | 'android' | null;
  /** Ticket stage: what Expo said when the push was handed to it. */
  status: PushCampaignRecipientStatus;
  error: string | null;
  /** Receipt stage; null until known (or when the ticket was not accepted). */
  receipt_status: PushReceiptStatus | null;
  receipt_error: string | null;
  created_at: string;
  /** Embedded for display; null when the member has since been hard-deleted. */
  member: {
    first_name: string | null;
    last_name: string | null;
    membership_number: string | null;
    deleted_at: string | null;
  } | null;
}

/** get_push_audience_preview() result. Every number is an UPPER bound on reach. */
export interface PushAudiencePreview {
  members_matched: number;
  members_reachable: number;
  tokens: number;
  tokens_ios: number;
  tokens_android: number;
}

/** One row of get_push_audience_members(): who a segment resolves to. */
export interface PushAudienceMember {
  id: string;
  first_name: string | null;
  last_name: string | null;
  membership_number: string | null;
  /** Registered push devices; 0 means the push cannot reach this member. */
  devices: number;
}

export interface PushAudienceMembers {
  total: number;
  /** Sorted by first name A→Z, then last name, in SQL. */
  members: PushAudienceMember[];
}

export interface SendPushCampaignInput {
  title: string;
  body: string;
  audience: PushAudience;
  audience_label: string;
  expected_members_reachable: number;
}

export interface SendPushCampaignResult {
  campaign_id: string;
  status: PushCampaignStatus;
  members_matched: number;
  members_reachable: number;
  tokens_targeted: number;
  sent_count: number;
  failed_count: number;
  tokens_removed: number;
  auditLogged: boolean;
}
